// src/components/SPanel/Home/useStudentHomeData.js
//
// Datele blocurilor de pe pagina principală a elevului, în afară de lecții
// (acelea vin din calendarul elevului, în SPanel). Fiecare sursă se încarcă
// separat: dacă una cade, blocul ei rămâne gol, celelalte merg.

import { useCallback, useEffect, useState } from "react";

import {
   getPracticeActivity,
   getPracticeQuestionStats,
   getStudentExamHistory,
} from "../../../api/examService";
import {
   getNotifications,
   getUnreadNotificationsCount,
} from "../../../api/notificationsService";
import { getRecommendedVideos } from "../../../api/recommendedVideosService";
import {
   getCurrentPopup,
   subscribePopup,
} from "../../Common/popupUIStore";
import { PRACTICE_TICKET_IDS } from "../../../utils/practiceStatsSummary";
import { mapRecommendedVideo } from "../../../utils/youtube";

const VIDEOS_SHOWN = 3;

function toMs(value) {
   const ms = value ? new Date(value).getTime() : 0;
   return Number.isFinite(ms) ? ms : 0;
}

const NOTIFICATIONS_SHOWN = 4;
const EXAM_ATTEMPTS_SHOWN = 10;

function mapExam(exam) {
   const status = String(exam.status || "").toUpperCase();
   return {
      id: exam.id,
      status,
      passed: status === "COMPLETED" || status === "PASSED",
      inProgress: status === "IN_PROGRESS",
      score: Number(exam.score) || 0,
      total: Number(exam.totalQuestions ?? exam.total) || 0,
      passScore: Number(exam.passScore) || 0,
      at: exam.completedAt || exam.startedAt || null,
   };
}

/** Ultima încercare la examenul teoretic și ultimele câteva, de la veche la nouă. */
function theoryExams(page) {
   const items = Array.isArray(page)
      ? page
      : page?.data || page?.items || page?.results || [];
   const exams = items.map(mapExam);
   return {
      attempts: Number(page?.pagination?.total) || items.length,
      last: exams[0] || null,
      history: exams.slice().reverse(),
   };
}

export default function useStudentHomeData() {
   const [practice, setPractice] = useState(null);
   const [notifications, setNotifications] = useState({
      loaded: false,
      unread: 0,
      items: [],
   });
   const [videos, setVideos] = useState({ loaded: false, total: 0, items: [] });
   const [theory, setTheory] = useState({
      loaded: false,
      last: null,
      attempts: 0,
      history: [],
   });
   // activitatea pe zile: 26 de săptămâni (harta) și graficul pe zile
   const [activity, setActivity] = useState({ loaded: false, days: [], learnedBefore: 0 });

   const loadNotifications = useCallback(async () => {
      try {
         const [count, page] = await Promise.all([
            getUnreadNotificationsCount(),
            getNotifications({
               page: 1,
               limit: NOTIFICATIONS_SHOWN,
               unreadOnly: true,
            }),
         ]);
         setNotifications({
            loaded: true,
            unread: Number(count) || 0,
            items: page.items || [],
         });
      } catch {
         setNotifications((prev) => ({ ...prev, loaded: true }));
      }
   }, []);

   useEffect(() => {
      let alive = true;

      getPracticeQuestionStats(PRACTICE_TICKET_IDS)
         .then((data) => alive && setPractice(data || null))
         .catch(() => {});

      getRecommendedVideos({ page: 1, limit: 50 })
         .then((page) => {
            if (!alive) return;
            const items = (page.items || [])
               .map((row, index) => mapRecommendedVideo(row, index))
               .filter(Boolean)
               .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
            setVideos({
               loaded: true,
               total: Math.max(Number(page.total) || 0, items.length),
               items: items.slice(0, VIDEOS_SHOWN),
            });
         })
         .catch(() => alive && setVideos((prev) => ({ ...prev, loaded: true })));

      getStudentExamHistory({ page: 1, limit: EXAM_ATTEMPTS_SHOWN })
         .then((page) => alive && setTheory({ loaded: true, ...theoryExams(page) }))
         .catch(() => alive && setTheory((prev) => ({ ...prev, loaded: true })));

      getPracticeActivity(182)
         .then((data) => {
            if (!alive) return;
            setActivity({
               loaded: true,
               days: Array.isArray(data?.days) ? data.days : [],
               learnedBefore: Number(data?.learnedBefore) || 0,
            });
         })
         .catch(() => alive && setActivity((prev) => ({ ...prev, loaded: true })));

      loadNotifications();
      return () => {
         alive = false;
      };
   }, [loadNotifications]);

   // după ce elevul închide fereastra de notificări (le-a citit), lista se reface
   useEffect(() => {
      let wasOpen = getCurrentPopup()?.type === "notifications";
      return subscribePopup(() => {
         const isOpen = getCurrentPopup()?.type === "notifications";
         if (wasOpen && !isOpen) loadNotifications();
         wasOpen = isOpen;
      });
   }, [loadNotifications]);

   useEffect(() => {
      const onVisible = () => {
         if (document.visibilityState === "visible") loadNotifications();
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => document.removeEventListener("visibilitychange", onVisible);
   }, [loadNotifications]);

   return { practice, notifications, videos, theory, activity };
}
